import React, { useRef } from 'react';
import {
  Download, X, Camera, 
  BedDouble, Bath, WavesIcon, 
  ShieldIcon, Snowflake, Wifi,
  TreePine, Home, FlameIcon, Layers
} from 'lucide-react';

export default function FichaTecnica({ 
  propiedad, 
  imagenesProcesadas, 
  onClose, 
  getSafeImageUrl,
  getImageUrl,
  selectedImage,
  setSelectedImage,
  handleDownloadPDF
}) {
  const fichaRef = useRef(null);
  const c = propiedad.caracteristicas || {};

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="max-w-5xl w-full h-[95vh] overflow-y-auto bg-slate-50 rounded-2xl shadow-2xl border border-slate-200">
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200 p-4 sm:p-6 flex flex-col sm:flex-row justify-between items-center gap-4 print:hidden">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Ficha Técnica</h1>
            <div className="text-slate-500 text-sm font-medium flex items-center gap-2">
              <span className="truncate max-w-[200px] sm:max-w-md">{propiedad.titulo}</span>
              <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold uppercase tracking-wider">
                ID: PRO-{propiedad.id.toString().padStart(4, '0')}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={handleDownloadPDF}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 active:scale-95 transition-all shadow-sm font-medium"
            >
              <Download size={18} />
              <span>PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-red-500 hover:border-red-100 hover:bg-red-50 transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Contenido de la Ficha */}
        <div ref={fichaRef} className="p-6 sm:p-10 space-y-10 bg-white print:p-0">
          
          {/* Sección de Imágenes */}
          <section className="space-y-4">
            <div className="flex items-end justify-between border-b border-slate-100 pb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                <span className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Camera size={20} /></span>
                Galería Visual
              </h2>
              <span className="text-slate-400 text-xs font-bold uppercase tracking-widest">
                {imagenesProcesadas.length} Archivos
              </span>
            </div>
            
            {imagenesProcesadas.length > 0 ? (
              <div className="grid grid-cols-12 gap-4">
                {/* Imagen Principal */}
                <div className="col-span-12 lg:col-span-9">
                  <div className="relative aspect-video rounded-2xl overflow-hidden shadow-inner bg-slate-100 group">
                    <img
                      src={getSafeImageUrl(imagenesProcesadas[selectedImage])}
                      alt="Vista propiedad"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => {
                        console.error('Error cargando imagen principal en ficha:', imagenesProcesadas[selectedImage]);
                        e.target.src = 'https://via.placeholder.com/800x600?text=Imagen+No+Disponible';
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full">
                      {selectedImage + 1} / {imagenesProcesadas.length}
                    </div>
                  </div>
                </div>
                
                {/* Miniaturas */}
                <div className="col-span-12 lg:col-span-3 flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:max-h-[450px] scrollbar-hide print:hidden">
                  {imagenesProcesadas.map((img, index) => {
                    const imageObj = img || {};
                    const imageUrl = getSafeImageUrl(img);
                    
                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`relative flex-shrink-0 w-20 h-20 lg:w-full lg:h-24 rounded-xl overflow-hidden transition-all duration-300 ${
                          selectedImage === index 
                            ? 'ring-2 ring-blue-500 ring-offset-2 scale-[0.98]' 
                            : 'opacity-60 hover:opacity-100 border border-slate-200'
                        }`}
                      >
                        <img 
                          src={imageUrl} 
                          className="w-full h-full object-cover" 
                          alt="" 
                          onError={(e) => {
                            console.error('Error cargando miniatura en ficha:', img);
                            e.target.src = 'https://via.placeholder.com/100x100?text=Imagen';
                          }}
                        />
                        
                        {/* Indicador de tags */}
                        {imageObj.tags && imageObj.tags.length > 0 && (
                          <div className="absolute bottom-1 left-1 right-1">
                            <div className="flex justify-center gap-0.5">
                              {imageObj.tags.slice(0, 3).map((tag, i) => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Indicador de descripción */}
                        {imageObj.descripcion && (
                          <div className="absolute top-1 left-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
                <Camera className="w-12 h-12 text-slate-300 mb-2" />
                <p className="text-slate-400 font-medium">No hay fotos disponibles</p>
              </div>
            )}
          </section>

          {/* Info General y Características */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Columna Izquierda: Datos */}
            <section className="space-y-6">
              <h3 className="text-sm font-black text-blue-600 uppercase tracking-[0.2em]">Especificaciones</h3>
              <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 space-y-4">
                {[
                  { label: 'Precio de Venta', value: `$${Number(propiedad.precio).toLocaleString()} MXN`, bold: true, color: 'text-blue-700' },
                  { label: 'Ubicación', value: propiedad.ubicacion },
                  { label: 'Zona/Barrio', value: propiedad.zona || 'N/A' },
                  { label: 'Operación', value: c.tipo_operacion || 'Venta' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-start border-b border-slate-200/50 pb-2 last:border-0 last:pb-0">
                    <span className="text-slate-500 text-sm">{item.label}</span>
                    <span className={`text-sm font-semibold text-right max-w-[200px] ${item.color || 'text-slate-800'}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Columna Derecha: Iconos Rápidos */}
            <section className="space-y-6">
              <h3 className="text-sm font-black text-blue-600 uppercase tracking-[0.2em]">Dimensiones y Áreas</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-white border border-slate-200 rounded-2xl flex items-center gap-4">
                  <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><BedDouble size={20} /></div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{c.recamaras || 0}</p>
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Habitaciones</p>
                  </div>
                </div>
                <div className="p-4 bg-white border border-slate-200 rounded-2xl flex items-center gap-4">
                  <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><Bath size={20} /></div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">{c.banos || 0}</p>
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Baños</p>
                  </div>
                </div>
                <div className="p-4 bg-slate-900 text-white rounded-2xl flex flex-col justify-center">
                  <p className="text-lg font-bold">{c.area ? `${c.area} m²` : 'N/A'}</p>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Construcción</p>
                </div>
                <div className="p-4 bg-slate-100 text-slate-900 rounded-2xl flex flex-col justify-center border border-slate-200">
                  <p className="text-lg font-bold">{c.terreno ? `${c.terreno} m²` : 'N/A'}</p>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Terreno</p>
                </div>
              </div>
            </section>
          </div>

          {/* Amenidades */}
          <section className="space-y-4">
            <h3 className="text-sm font-black text-blue-600 uppercase tracking-[0.2em]">Comodidades Incluidas</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { show: c.alberca, label: 'Alberca', icon: <WavesIcon size={14} /> },
                { show: c.vigilancia, label: 'Seguridad 24/7', icon: <ShieldIcon size={14} /> },
                { show: c.aire_acondicionado, label: 'A/C', icon: <Snowflake size={14} /> },
                { show: c.pet_friendly, label: 'Pet Friendly', icon: '🐾' },
                { show: c.internet, label: 'Fibra Óptica', icon: <Wifi size={14} /> },
                { show: c.jardin, label: 'Jardín', icon: <TreePine size={14} /> },
                { show: c.terraza, label: 'Terraza', icon: <Home size={14} /> },
                { show: c.cocina_equipada, label: 'Cocina Equipada', icon: <FlameIcon size={14} /> },
                { show: c.amueblado, label: 'Amueblado', icon: <Layers size={14} /> },
              ].map((amenity, i) => amenity.show && (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
                  <span className="text-blue-500">{amenity.icon}</span>
                  {amenity.label}
                </div>
              ))}
            </div>
          </section>

          {/* Descripción */}
          <section className="space-y-4">
            <h3 className="text-sm font-black text-blue-600 uppercase tracking-[0.2em]">Acerca de la Propiedad</h3>
            <div className="relative p-6 bg-slate-50 rounded-2xl border-l-4 border-blue-500">
              <p className="text-slate-600 leading-relaxed text-base italic">
                "{propiedad.descripcion || 'Sin descripción adicional.'}"
              </p>
            </div>
          </section>

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t-2 border-slate-100 flex flex-col items-center text-center gap-4">
            <div className="flex items-center gap-2 font-black text-xl tracking-tighter text-slate-900">
              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white text-xs">IP</div>
              INMOBILIARIA <span className="text-blue-600">PREMIUM</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2 text-slate-500 text-xs font-medium">
              <p className="flex items-center justify-center gap-2 truncate">📍 {propiedad.ubicacion}</p>
              <p className="flex items-center justify-center gap-2">📞 999 999 9999</p>
              <p className="flex items-center justify-center gap-2">✉️ contacto@inmobiliaria.com</p>
              <p className="flex items-center justify-center gap-2 font-bold text-slate-400 uppercase">www.tuweb.com</p>
            </div>
            <p className="mt-6 text-[10px] text-slate-300 uppercase tracking-widest">
              Documento generado el {new Date().toLocaleDateString()} • {new Date().toLocaleTimeString()}
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}